import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, X } from 'lucide-react';

const EXPLANATIONS = {
  research: {
    title: 'Research Engine',
    body: 'Type any company name, person, or paste a URL. BREACH runs 13+ web searches, resolves contacts, and uses AI to build a full intelligence report — Mission & Programmes, Focus Areas, Public Activity & Content, Funding Relationships & Capacity, Fit Assessment (Prospective Client / Funder-Investor / Multiplier / Strategic Connector), contacts, BD angles, and pitch hooks. Results are saved as target records you can track in your pipeline.',
  },
  outreach: {
    title: 'Outreach Generator',
    body: 'Select a target and outreach type, then paste any prior conversation context. BREACH writes outreach in your voice using your cognitive profile, the target\'s research, and your org context. It also extracts actionable pipeline updates from the conversation.',
  },
  grants: {
    title: 'Funding Intelligence',
    body: 'Live, Tavily-powered discovery across your Opportunity Universe — foundation & government grants, accelerators, competitions & challenge funds, corporate programmes, awards, and (for startups) investment. Queries are built from your organisation profile\'s sector tags + geography, so results are filtered to what you can actually apply for. Each result is scored for fit (1–10) and ranked by deadline (soonest first): under 14 days shows CRITICAL (red), 14–30 days ATTENTION (amber), nothing is ever hidden. Your last scan is saved and shown instantly via the "Last scan" button — it only re-runs (and uses API credits) when you press "Refresh Funding Intelligence". Your TOP 3 ACTIONS THIS WEEK are highlighted automatically.',
  },
  events: {
    title: 'Market Events',
    body: 'Live, Tavily-powered discovery of upcoming conferences, summits, forums, and speaking opportunities relevant to your organisation. Queries are built from your profile\'s sector tags + geography, and every detail — dates, location, website, deadlines — is extracted strictly from the source (no synthesis, no invented dates). Events show chronologically, soonest first; only genuinely upcoming events (7+ days out) appear, and CFP/registration deadlines closing within 7 days are flagged "CLOSING SOON". Use "Filter by category" to narrow by event type. Your last scan is saved (14-day cache) and shown instantly — it only re-runs (and uses API credits) when you press "Scan for New Events". On any card, "Show Targets" surfaces which of your existing targets are likely to attend (reasoned from profile fit, not confirmed), and "Pre-event Outreach" jumps to the Outreach engine pre-loaded with the event context.',
  },
  context_import: {
    title: 'Import Context',
    body: 'Paste any communication — emails, WhatsApp threads, meeting notes, or brain dumps — and BREACH extracts every named person, organisation, pipeline signal, and next action. You can also upload PDF, DOCX, TXT, or image files for extraction. All extracted items appear as individual proposed field changes that you can accept or reject one by one before anything is applied to the card. If extraction appears to hang, try again or paste shorter content — the AI processes in the background and results typically appear within 30 seconds.',
  },
  briefing: {
    title: 'Meeting Briefs',
    body: 'Generates a concise briefing pack for any pipeline record — summarising the target\'s research, your relationship history, key talking points, and suggested asks. Perfect for preparing before calls, meetings, or events.',
  },
  projects: {
    title: 'Projects',
    body: 'Your active projects and products live here — every pipeline opportunity maps back to a project. Click any project to see its full intelligence profile, all associated targets with their project-specific pitch angles, and quick-access outreach generation. Use Research Targets for This Project to find new targets via the Research Engine. Use View Pipeline to see all pipeline activity for this project in the kanban view. Use Generate Outreach on any target card to draft an outreach message pre-loaded with the right angle for this specific project.',
  },
  export: {
    title: 'Intelligence PDF Export',
    body: 'Downloads the complete Target Intelligence Card as a PDF — every section including Professional Background, Current Roles, Career History, Mission Alignment, Estimated Capacity, Evidence of Engagement, Media Presence, BD Angles, Engagement Approach, all Suggested Pitch Hooks, Red Flag Check, Awards, Key Contacts, Project Alignment, and Import History. No section is omitted — if data is unavailable it shows "Not found in public record." Perfect for sharing before meetings.',
  },
};

const HowDoesThisWorkPill = ({ feature }) => {
  const [open, setOpen] = useState(false);
  const info = EXPLANATIONS[feature];
  if (!info) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#0A1628] border border-[#1A2F4C] text-[#A3B5C7] hover:text-[#00D4C8] hover:border-[#00D4C8]/30 transition-all text-xs"
        data-testid={`how-it-works-${feature}`}
      >
        <HelpCircle className="w-3 h-3" />
        How does this work?
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setOpen(false)}
          >
            <div className="absolute inset-0 bg-[#0A1628]/70 backdrop-blur-sm" />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md bg-[#0E1C33] border border-[#1A2F4C] rounded-xl p-6 shadow-2xl"
              data-testid={`how-modal-${feature}`}
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-4 right-4 p-1 rounded hover:bg-white/10 text-[#A3B5C7] hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <h3 className="font-heading text-lg text-white mb-3">{info.title}</h3>
              <p className="text-sm text-[#D4E2F0] leading-relaxed">{info.body}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default HowDoesThisWorkPill;
